import React, { useMemo, useEffect, useState, useRef } from "react";
import { CRow, CCol } from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSearch, cilLayers, cilFilter, cilMap } from "@coreui/icons";

const PRIMARY_COLOR = "#5856d6";
const PRIMARY_GLOW = "rgba(88, 86, 214, 0.15)";
const PRIMARY_SHADOW = "rgba(88, 86, 214, 0.25)";
const PRIMARY_BG_LIGHT = "rgba(88, 86, 214, 0.08)";

export const RenderVehicleIcon = ({ icon, iconUrl, style = {}, className = "" }) => {
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt="icon"
        className={className}
        style={{ width: "20px", height: "20px", objectFit: "contain", verticalAlign: "middle", ...style }}
      />
    );
  }
  return <span className={className} style={{ fontSize: "1.1rem", lineHeight: 1, ...style }}>{icon || "🚜"}</span>;
};

// Кастомный селект с точной высотой 38px и фирменной подсветкой #5856d6
const CustomFilterDropdown = ({
  icon,
  value,
  onChange,
  options,
  placeholder = "Выберите...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.value === value);

  return (
    <div ref={dropdownRef} className="position-relative w-100" style={{ minWidth: 0 }}>
      <div
        className="d-flex align-items-center justify-content-between px-3 bg-white border rounded shadow-sm"
        style={{
          height: "38px",
          cursor: "pointer",
          userSelect: "none",
          borderColor: isOpen ? PRIMARY_COLOR : "#e2e8f0",
          boxShadow: isOpen ? `0 0 0 3px ${PRIMARY_GLOW}` : undefined,
          transition: "all 0.15s ease",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="d-flex align-items-center text-truncate me-2">
          {icon && (
            <span
              className="me-2 d-flex align-items-center flex-shrink-0"
              style={{ color: isOpen || selectedOption?.value ? PRIMARY_COLOR : "#94a3b8" }}
            >
              {icon}
            </span>
          )}
          <span className="fw-semibold text-truncate text-dark" style={{ fontSize: "0.875rem" }}>
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <span
          className="text-muted small ms-1 flex-shrink-0"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            fontSize: "10px",
          }}
        >
          ▼
        </span>
      </div>

      {isOpen && (
        <div
          className="position-absolute start-0 w-100 bg-white border rounded shadow-lg mt-1"
          style={{
            zIndex: 1050,
            maxHeight: "260px",
            overflowY: "auto",
            borderColor: "#e2e8f0",
            padding: "4px",
          }}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className="d-flex align-items-center justify-content-between px-3 py-2 rounded small"
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected ? PRIMARY_BG_LIGHT : "transparent",
                  color: isSelected ? PRIMARY_COLOR : "#334155",
                  fontWeight: isSelected ? "700" : "500",
                  transition: "background-color 0.1s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "#f8fafc";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                }}
                onClick={() => {
                  onChange(opt.value);
                  setIsOpen(false);
                }}
              >
                <span className="text-truncate">{opt.label}</span>
                {isSelected && <span className="fw-bold ms-2" style={{ color: PRIMARY_COLOR }}>✓</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilterControls = ({
  t,
  isSuperuser,
  filteredWorkZones,
  selectedZoneFilter,
  setSelectedZoneFilter,
  vehicleTypes = [],
  selectedTypeFilter = "",
  setSelectedTypeFilter,
  devices = [],
  selectedOrgFilter,
  searchQuery,
  setSearchQuery,
  setSelectedOrgFilter,
  uniqueOrganizations,
  sortBy,
  setSortBy,
}) => {
  const [searchFocused, setSearchFocused] = useState(false);

  const scopedDevices = useMemo(() => {
    return devices.filter((d) =>
      isSuperuser && selectedOrgFilter ? d.organization_name === selectedOrgFilter : true
    );
  }, [devices, isSuperuser, selectedOrgFilter]);

  const availableVehicleTypes = useMemo(() => {
    return vehicleTypes.filter((vt) =>
      scopedDevices.some((d) => d.vehicle_type === vt.id)
    );
  }, [vehicleTypes, scopedDevices]);

  useEffect(() => {
    if (
      selectedTypeFilter &&
      !availableVehicleTypes.some((vt) => vt.id.toString() === selectedTypeFilter)
    ) {
      setSelectedTypeFilter("");
    }
  }, [availableVehicleTypes, selectedTypeFilter, setSelectedTypeFilter]);

  const sortOptions = [
    { value: "last_seen_desc", label: "Сначала активные (Недавние)" },
    { value: "last_seen_asc", label: "Сначала неактивные (Давние)" },
    { value: "name_asc", label: "По алфавиту (А - Я)" },
    { value: "name_desc", label: "По алфавиту (Я - А)" },
  ];

  const orgOptions = useMemo(() => [
    { value: "", label: "Все организации" },
    ...uniqueOrganizations.map((org) => ({ value: org, label: org }))
  ], [uniqueOrganizations]);

  // Стилизация чипсов с фирменным фиолетовым цветом кнопки "Открыть" (#5856d6)
  const getPillStyle = (isActive) => ({
    height: "38px",
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "999px",
    padding: "0 18px",
    fontWeight: "600",
    fontSize: "0.875rem",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: isActive ? `1px solid ${PRIMARY_COLOR}` : "1px solid #e2e8f0",
    backgroundColor: isActive ? PRIMARY_COLOR : "#ffffff",
    color: isActive ? "#ffffff" : "#475569",
    boxShadow: isActive ? `0 4px 12px ${PRIMARY_SHADOW}` : "0 1px 3px rgba(0,0,0,0.05)",
  });

  return (
    <div className="mb-4">
      {/* 1. ФИЛЬТР ПО ТИПАМ ТЕХНИКИ */}
      {availableVehicleTypes.length > 1 && (
        <div className="d-flex flex-wrap gap-2 mb-3 justify-content-center bg-white p-2 rounded shadow-sm border">
          <button
            type="button"
            style={getPillStyle(selectedTypeFilter === "")}
            onMouseEnter={(e) => {
              if (selectedTypeFilter !== "") {
                e.currentTarget.style.borderColor = PRIMARY_COLOR;
                e.currentTarget.style.color = PRIMARY_COLOR;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedTypeFilter !== "") {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.color = "#475569";
              }
            }}
            onClick={() => setSelectedTypeFilter("")}
          >
            Все виды техники
            <span
              style={{
                backgroundColor: selectedTypeFilter === "" ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                color: selectedTypeFilter === "" ? "#ffffff" : "#64748b",
                borderRadius: "999px",
                padding: "2px 8px",
                fontSize: "0.75rem",
                marginLeft: "8px",
              }}
            >
              {scopedDevices.length}
            </span>
          </button>

          {availableVehicleTypes.map((vt) => {
            const count = scopedDevices.filter((d) => d.vehicle_type === vt.id).length;
            const isActive = selectedTypeFilter === vt.id.toString();

            return (
              <button
                key={vt.id}
                type="button"
                style={getPillStyle(isActive)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = PRIMARY_COLOR;
                    e.currentTarget.style.color = PRIMARY_COLOR;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                onClick={() => setSelectedTypeFilter(vt.id.toString())}
              >
                <span className="me-2 d-flex align-items-center">
                  <RenderVehicleIcon icon={vt.icon} iconUrl={vt.icon_url} />
                </span>
                <span>{vt.name}</span>
                <span
                  style={{
                    backgroundColor: isActive ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                    color: isActive ? "#ffffff" : "#64748b",
                    borderRadius: "999px",
                    padding: "2px 8px",
                    fontSize: "0.75rem",
                    marginLeft: "8px",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* 2. ФИЛЬТР ПО ОБЪЕКТАМ / УЧАСТКАМ */}
      {filteredWorkZones.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center bg-white p-2 rounded shadow-sm border">
          <button
            type="button"
            style={getPillStyle(selectedZoneFilter === "")}
            onMouseEnter={(e) => {
              if (selectedZoneFilter !== "") {
                e.currentTarget.style.borderColor = PRIMARY_COLOR;
                e.currentTarget.style.color = PRIMARY_COLOR;
              }
            }}
            onMouseLeave={(e) => {
              if (selectedZoneFilter !== "") {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.color = "#475569";
              }
            }}
            onClick={() => setSelectedZoneFilter("")}
          >
            Все объекты
            <span
              style={{
                backgroundColor: selectedZoneFilter === "" ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                color: selectedZoneFilter === "" ? "#ffffff" : "#64748b",
                borderRadius: "999px",
                padding: "2px 8px",
                fontSize: "0.75rem",
                marginLeft: "8px",
              }}
            >
              {scopedDevices.length}
            </span>
          </button>

          {filteredWorkZones.map((zone) => {
            const count = scopedDevices.filter((d) => d.work_zone === zone.id).length;
            const isActive = selectedZoneFilter === zone.id.toString();
            const labelSuffix =
              isSuperuser && !selectedOrgFilter && zone.organization_name
                ? ` (${zone.organization_name})`
                : "";

            return (
              <button
                key={zone.id}
                type="button"
                style={getPillStyle(isActive)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = PRIMARY_COLOR;
                    e.currentTarget.style.color = PRIMARY_COLOR;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }
                }}
                onClick={() => setSelectedZoneFilter(zone.id.toString())}
              >
                <CIcon
                  icon={cilMap}
                  className="me-2"
                  style={{ color: isActive ? "#ffffff" : PRIMARY_COLOR, opacity: isActive ? 1 : 0.7 }}
                />
                <span>{zone.name}{labelSuffix}</span>
                <span
                  style={{
                    backgroundColor: isActive ? "rgba(255,255,255,0.25)" : "#f1f5f9",
                    color: isActive ? "#ffffff" : "#64748b",
                    borderRadius: "999px",
                    padding: "2px 8px",
                    fontSize: "0.75rem",
                    marginLeft: "8px",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* 3. ПОИСК, ОРГАНИЗАЦИЯ И СОРТИРОВКА (СТРОГО 38px ВЫСОТА) */}
      <CRow className="g-3">
        <CCol md={isSuperuser ? 4 : 6}>
          <div
            className="d-flex align-items-center px-3 bg-white border rounded shadow-sm"
            style={{
              height: "38px",
              borderColor: searchFocused ? PRIMARY_COLOR : "#e2e8f0",
              boxShadow: searchFocused ? `0 0 0 3px ${PRIMARY_GLOW}` : undefined,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
          >
            <CIcon
              icon={cilSearch}
              className="me-2 flex-shrink-0"
              style={{ width: "16px", height: "16px", color: searchFocused ? PRIMARY_COLOR : "#94a3b8" }}
            />
            <input
              type="text"
              className="border-0 p-0 fw-semibold text-dark w-100"
              style={{
                outline: "none",
                fontSize: "0.875rem",
                backgroundColor: "transparent",
              }}
              placeholder="Поиск техники по имени..."
              value={searchQuery}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <span
                className="text-muted ms-2 flex-shrink-0"
                style={{ cursor: "pointer", fontSize: "14px", lineHeight: 1 }}
                onClick={() => setSearchQuery("")}
                title="Очистить"
              >
                ✕
              </span>
            )}
          </div>
        </CCol>

        {isSuperuser && (
          <CCol md={4}>
            <CustomFilterDropdown
              icon={<CIcon icon={cilLayers} style={{ width: "16px", height: "16px" }} />}
              value={selectedOrgFilter}
              onChange={setSelectedOrgFilter}
              options={orgOptions}
              placeholder="Все организации"
            />
          </CCol>
        )}

        <CCol md={isSuperuser ? 4 : 6}>
          <CustomFilterDropdown
            icon={<CIcon icon={cilFilter} style={{ width: "16px", height: "16px" }} />}
            value={sortBy}
            onChange={setSortBy}
            options={sortOptions}
            placeholder="Сортировка"
          />
        </CCol>
      </CRow>
    </div>
  );
};

export default FilterControls;